import { CategoryPackItems } from '@/types/category'
import { Unit } from '@/types/item'
import { PackItem } from '@/types/pack'

import { SYSTEM_UNIT } from './consts'
import { wornQuantity } from './worn'

const CONVERSION_FACTORS: Record<Unit, number> = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
}

/**
 * Converts a weight value between units.
 * Returns both the numeric value and a formatted display string.
 */
export function convertWeight(weight: number, fromUnit: Unit, toUnit: Unit) {
  const weightInGrams = weight * CONVERSION_FACTORS[fromUnit]
  const convertedWeight = weightInGrams / CONVERSION_FACTORS[toUnit]

  return {
    weight: convertedWeight,
    display: `${convertedWeight.toFixed(2)} ${toUnit}`,
  }
}

/**
 * Converts a weight value between units, returning only the numeric result.
 */
export function convertWeightValue(
  weight: number,
  fromUnit: Unit,
  toUnit: Unit
): number {
  const weightInGrams = weight * CONVERSION_FACTORS[fromUnit]
  return weightInGrams / CONVERSION_FACTORS[toUnit]
}

export const getConversionUnit = (unit_system: SYSTEM_UNIT): Unit => {
  return unit_system === 'METRIC' ? 'kg' : 'lb'
}

export const getItemDisplayUnit = (unit_system: SYSTEM_UNIT): Unit => {
  return unit_system === 'METRIC' ? 'g' : 'oz'
}

/**
 * Formats a summed weight (in grams) for display, using the unit system
 * from the user's preferences: g/kg for metric, oz/lb for imperial.
 */
export function formatTotalWeight(
  totalGrams: number,
  unitSystem: SYSTEM_UNIT
): string {
  if (unitSystem === 'METRIC') {
    return totalGrams >= 1000
      ? `${(totalGrams / 1000).toFixed(1)} kg`
      : `${Math.round(totalGrams)} g`
  }
  const totalOz = totalGrams / CONVERSION_FACTORS.oz
  return totalOz >= 16
    ? `${(totalOz / 16).toFixed(1)} lb`
    : `${Math.round(totalOz)} oz`
}

const PROMOTE_THRESHOLDS: Partial<
  Record<Unit, { threshold: number; to: Unit }>
> = {
  g: { threshold: 1000, to: 'kg' },
  oz: { threshold: 16, to: 'lb' },
}

/**
 * Formats an item weight for display, promoting to the larger unit
 * (g -> kg, oz -> lb) when the value exceeds a natural threshold.
 */
export function formatItemWeight(
  weight: number,
  fromUnit: Unit,
  targetItemUnit: Unit
): string {
  const converted = convertWeight(weight, fromUnit, targetItemUnit)
  const promo = PROMOTE_THRESHOLDS[targetItemUnit]

  if (promo && converted.weight >= promo.threshold) {
    const promoted = convertWeight(weight, fromUnit, promo.to)
    return `${promoted.weight.toFixed(2)} ${promo.to}`
  }

  const rounded =
    targetItemUnit === 'g'
      ? Math.round(converted.weight)
      : parseFloat(converted.weight.toFixed(2))
  return `${rounded} ${targetItemUnit}`
}

export const oppositeSystem = (system: SYSTEM_UNIT): SYSTEM_UNIT =>
  system === 'METRIC' ? 'IMPERIAL' : 'METRIC'

export const unitSystemOf = (unit: Unit): SYSTEM_UNIT =>
  unit === 'g' || unit === 'kg' ? 'METRIC' : 'IMPERIAL'

/**
 * The app's three ways of showing a weight:
 * - item: g/oz, promoted to kg/lb past 1000 g / 16 oz (gear rows)
 * - total: whole g/oz, or kg/lb to one decimal (closet and category totals)
 * - precise: kg/lb to two decimals (pack breakdowns)
 * - small: whole g, or oz to one decimal, never promoted (food macros)
 */
export type WeightFormat = 'item' | 'total' | 'precise' | 'small'

/** Formats a weight (in grams) in a unit system using one of the styles above. */
export function formatWeightIn(
  grams: number,
  system: SYSTEM_UNIT,
  format: WeightFormat = 'item'
): string {
  if (format === 'total') return formatTotalWeight(grams, system)
  if (format === 'small') {
    return system === 'METRIC'
      ? `${Math.round(grams)} g`
      : `${(grams / CONVERSION_FACTORS.oz).toFixed(1)} oz`
  }
  if (format === 'precise') {
    const unit = getConversionUnit(system)
    return `${convertWeightValue(grams, 'g', unit).toFixed(2)} ${unit}`
  }
  return formatItemWeight(grams, 'g', getItemDisplayUnit(system))
}

/**
 * Sums the total weight of pack items, converting each to the target unit
 * and multiplying by quantity.
 */
export function sumPackItemWeights(
  items: PackItem[],
  toUnit: Unit
): number {
  return items.reduce((total, { item, quantity }) => {
    const converted = convertWeightValue(item.weight || 0, item.unit, toUnit)
    return total + converted * quantity
  }, 0)
}

export type WeightBreakdown = {
  worn: number
  consumable: number
  base: number
  total: number
}

/**
 * Calculates a full weight breakdown (worn, consumable, base, total)
 * for a set of pack items.
 */
export function calculateWeightBreakdown(
  items: PackItem[],
  toUnit: Unit
): WeightBreakdown {
  // Same split as the API (utils/pack_weight.py), mobile and the public
  // site: worn units are worn; the remaining units are consumable or base.
  return items.reduce(
    (acc, pi) => {
      const { item, quantity } = pi
      const unit = convertWeightValue(item.weight || 0, item.unit, toUnit)
      const worn = unit * wornQuantity(pi)
      const rest = unit * quantity - worn
      return {
        worn: acc.worn + worn,
        consumable: item.consumable ? acc.consumable + rest : acc.consumable,
        base: item.consumable ? acc.base : acc.base + rest,
        total: acc.total + unit * quantity,
      }
    },
    { worn: 0, consumable: 0, base: 0, total: 0 }
  )
}

/**
 * Sums the total calories for a set of pack items, multiplying by quantity.
 */
export function sumPackItemCalories(items: PackItem[]): number {
  return items.reduce(
    (total, { item, quantity }) => total + (item.calories || 0) * quantity,
    0
  )
}

/**
 * Calculates total weight per category for a list of categorized pack items.
 */
export function calculateCategoryWeights(
  categories: CategoryPackItems[],
  toUnit: Unit
) {
  return categories.map(category => ({
    label: category.category?.category.name || 'Uncategorized',
    value: sumPackItemWeights(category.items, toUnit),
    unit: toUnit,
  }))
}
